import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openSettings } from 'react-native-permissions';

import { useNavigation } from '@react-navigation/native';

import ExclamationMark from '@/assets/icons/exclamation-mark-circle';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { GeoComplyError } from '@/feature/betslip-sbk/types';
import { getGeoToken } from '@/feature/betslip-sbk/utils/bet-submission/generate-bet-payload';
import { useTermsAndConditionsModal } from '@/feature/terms-and-conditions/components/TermsAndConditionModalProvider';
import { useHasUserAcceptedProductTerms } from '@/feature/terms-and-conditions/hooks/use-has-user-accepted-product-terms';
import { CreateEntryAccuracyError, validateLocationAccuracy } from '@/hooks/use-entries';
import { useUserStore } from '@/hooks/use-user-store';
import { useWallet } from '@/hooks/use-wallet';
import { getChannel } from '@/utils/bet-channel';
import { logger } from '@/utils/logging';

import { useGameSettingsStore } from './use-game-settings';
import { useGeocomplyErrorProps } from './use-geocomply-error';
import { StartGameError, useStartGame } from './use-start-game';

const ARCADE_START_LOG_TAG = '[Arcade Start]';
const ARCADE_TERMS_LOG_TAG = '[Arcade Terms]';

const logFailMessage = (error: unknown, type: string) =>
    logger.warn(ARCADE_START_LOG_TAG, 'ARCADE_START_FAIL', { type }, error);

export const useStartGameAction = () => {
    const { t } = useTranslation(['common', 'wallet', 'arcade_game_details']);
    const { realMoneyTotal, betrBucks } = useWallet();
    const { mutateAsync: startGame, isPending } = useStartGame();
    const selectedStake = useGameSettingsStore(state => state.selectedStake);
    const gameType = useGameSettingsStore(state => state.gameMode);
    const gameId = useGameSettingsStore(state => state.gameId);
    const useBetrBucks = useGameSettingsStore(state => state.useBetrBucks);
    const profile = useUserStore(state => state.profile);
    const navigation = useNavigation();
    const { showInfoSheet } = useAlerts();
    const { showTermsAndConditionsModal } = useTermsAndConditionsModal();
    const [isLoading, setIsLoading] = useState(false);
    const { isAccepted: hasAcceptedProductTerms } = useHasUserAcceptedProductTerms();
    const { getGeocomplyErrorProps } = useGeocomplyErrorProps();

    const handleStartGameError = useCallback(
        (error: unknown, errorAlreadyLogged?: boolean) => {
            let title = t('common:simple_error_message');
            let description: string | React.ReactNode = t('common:error_subtitle');

            let primaryLabel: string | undefined;
            let handlePrimaryPress: (() => void) | undefined;

            let secondaryLabel: string | undefined = t('common:got_it');
            let handleSecondaryPress: (() => void) | undefined;

            if (error instanceof GeoComplyError) {
                const errorProps = getGeocomplyErrorProps(error);
                title = errorProps.title;
                description = errorProps.description;
            } else if (error instanceof CreateEntryAccuracyError) {
                title = t('common:precise_location_failure');
                // keeping this as constant for now to avoid duplication
                description =
                    'Precise location must be turned on before you can place an entry.\n Go to app Settings → Permissions → Location and allow access and precise location.';
                handlePrimaryPress = () => {
                    openSettings();
                };
                primaryLabel = t('common:go_to_settings');
                secondaryLabel = undefined;
                handleSecondaryPress = undefined;
            } else if (error instanceof StartGameError) {
                const errorCode = error.code;
                const playLimitErrorCodes = [
                    'custom_single_wager_amount_per_line_is_above_customer_max_single_wager',
                    'wager_limit_reached',
                    'wager_limit_reached_day',
                    'wager_limit_reached_week',
                    'wager_limit_reached_month',
                ];
                if (playLimitErrorCodes.includes(errorCode)) {
                    title = t('common:oops');
                    description = t('arcade_game_details:play_limit_reached');
                }
                if (errorCode === 'terms_and_conditions_not_accepted') {
                    return; // Don't show error modal and don't want to log this error if user intentionally dismissed the terms and conditions modal
                }

                logFailMessage(error, 'start_game_error');
            } else {
                if (!errorAlreadyLogged) {
                    logFailMessage(error, 'unknown_error');
                }
            }

            showInfoSheet({
                title,
                description,
                secondaryLabel,
                buttonLabel: primaryLabel,
                handlePress: handlePrimaryPress,
                handleSecondaryPress,
                icon: <ExclamationMark />,
            });
        },
        [showInfoSheet, t, getGeocomplyErrorProps]
    );

    const checkUserAcceptedProductTerms = useCallback(async () => {
        if (!hasAcceptedProductTerms) {
            await new Promise<void>((resolve, reject) => {
                showTermsAndConditionsModal({
                    onSuccess: () => {
                        resolve();
                    },
                    onCancel: () => {
                        reject(new StartGameError('terms_and_conditions_not_accepted'));
                    },
                    onTermsError: error => {
                        logger.debug(ARCADE_TERMS_LOG_TAG, 'Error accepting Arcade user terms and conditions', error);
                        reject(new StartGameError('terms_and_conditions_failed'));
                    },
                });
            });
        }
    }, [hasAcceptedProductTerms, showTermsAndConditionsModal]);

    const handleStartGame = useCallback(async () => {
        setIsLoading(true);

        try {
            await validateLocationAccuracy();
        } catch (error) {
            logFailMessage(error, 'location_accuracy_error');
            handleStartGameError(error, true);
            setIsLoading(false);
            return;
        }

        try {
            await checkUserAcceptedProductTerms();
        } catch (error) {
            handleStartGameError(error);
            setIsLoading(false);
            return;
        }

        let geotoken: string | undefined;
        try {
            geotoken = await getGeoToken();
        } catch (error) {
            logFailMessage(error, 'geo_comply_error');
            handleStartGameError(error, true);
            setIsLoading(false);
            return;
        }

        try {
            if (!geotoken) {
                throw new StartGameError('geotoken_missing');
            }
            if (!gameId || !gameType || !selectedStake) {
                throw new StartGameError('game_parameters_missing');
            }
            const startGamePayload = {
                game_id: gameId,
                game_mode: gameType,
                stake: {
                    stake: selectedStake.stake,
                    rake: selectedStake.rake,
                    currency: selectedStake.currency,
                },
                customer: {
                    person_id: profile.sub,
                    partner_user_id: profile.sub,
                    geotoken,
                },
                channel: getChannel(),
            };
            const resp = await startGame(startGamePayload);
            if (!resp.data?.session?.iframe_url) {
                throw new StartGameError('no_iframe_url_found');
            }
            logger.info(ARCADE_START_LOG_TAG, 'ARCADE_START_SUCCESS', { payload: startGamePayload });
            navigation.navigate('ArcadeGameWebView', {
                gameId,
                iframeUrl: resp.data.session.iframe_url,
                isPractice: gameType === 'practice',
                betExternalId: resp.data.session?.betGuid,
            });
        } catch (error) {
            handleStartGameError(error);
        } finally {
            setIsLoading(false);
        }
    }, [
        gameId,
        gameType,
        selectedStake,
        profile.sub,
        startGame,
        handleStartGameError,
        navigation,
        checkUserAcceptedProductTerms,
    ]);

    // Check for insufficient funds based on payment method
    const hasInsufficientRealMoney = selectedStake && !useBetrBucks && realMoneyTotal < selectedStake.stake;
    const hasInsufficientBetrBucks = selectedStake && useBetrBucks && betrBucks < selectedStake.stake;

    const isGameLoading = isLoading || isPending;

    const stakeAmount = selectedStake?.stake || 0;
    const rakeAmount = selectedStake?.rake || 0;
    const toWinAmount = stakeAmount * 2 - rakeAmount * 2;

    return {
        handleStartGame,
        isLoading: isGameLoading,
        hasInsufficientRealMoney,
        hasInsufficientBetrBucks,
        isUsingBetrBucks: useBetrBucks,
        isPractice: gameType === 'practice',
        stakeAmount,
        toWinAmount,
    };
};
